import { useVirtualizer } from '@tanstack/react-virtual';
import type { PdpOrderBy, SortDirection } from '@vizit/api';
import { Cell, Row } from '@vizit/atoms';
import { FEATURE_FLAGS } from '@vizit/constants';
import { useFeatureFlag } from '@vizit/feature-flags';
import { useVirtualizedLoadMore } from '@vizit/hooks';
import { ClipboardIcon } from '@vizit/icons';
import { LibraryEmptyState, type ProductListingData, ProductListingRow, type RowAction } from '@vizit/molecules';
import { cx } from '@vizit/theme';
import * as React from 'react';
import { Table, TableBody } from 'react-aria-components';
import { productTableWrapperClasses, tableClasses, tableContainerClasses } from './product-listing-table.classes';
import { ProductListingTableHeader } from './product-listing-table.header';
import { ProductListingTableSkeleton } from './product-listing-table.loading';
import { ProductListingTableThead } from './product-listing-table.th';

interface ProductListingTableProps {
  /**
   * Array of product data to display in the table
   *
   * @required
   * @type ProductListingData[]
   */
  products: ProductListingData[];
  /**
   * Callback fired when a row is clicked
   *
   * @optional
   * @type (asin: string) => void
   */
  onRowClick?: (asin: string) => void;
  /**
   * Callback fired when the "Contact support" link is clicked in error state
   *
   * @optional
   * @type () => void
   */
  onContactSupport?: () => void;
  /**
   * Additional CSS classes to apply to the table
   *
   * @optional
   * @type string
   */
  className?: string;
  /**
   * ARIA label for the table
   *
   * @optional
   * @type string
   * @default "Product listing table"
   */
  'aria-label'?: string;
  /**
   * Whether the table is in a loading state
   *
   * @optional
   * @type boolean
   * @default false
   */
  isLoading?: boolean;
  /**
   * Total known count of rows
   */
  totalCount: number;
  /**
   * Called when the list needs to load more (bottom reached)
   */
  onLoadMore?: () => void;
  /**
   * Whether the next page is being fetched
   */
  isFetchingNextPage?: boolean;
  /**
   * Import product dialog to display in the empty state
   * @type {React.ReactNode}
   * @optional
   */
  importProductDialog?: React.ReactNode;
  /**
   * Create product dialog to display in the new product menu
   * @type {React.ReactNode}
   * @optional
   */
  createProductDialog?: React.ReactNode;
  /**
   * Bulk import dialog to display in the new product menu
   * @type {React.ReactNode}
   * @optional
   */
  bulkImportDialog?: React.ReactNode;
  /**
   * Array of action buttons to display on row hover
   *
   * @optional
   * @type RowAction[]
   */
  rowActions?: RowAction[];
  /**
   * Callback executed when the Download CSV button is pressed
   *
   * @optional
   * @type {() => void}
   */
  onDownloadCsv?: () => void;
  /**
   * Whether the download action is currently running
   *
   * @optional
   * @type {boolean}
   * @default false
   */
  isDownloadingCsv?: boolean;
  /**
   * Whether the download button should be disabled
   *
   * @optional
   * @type {boolean}
   * @default false
   */
  isDownloadCsvDisabled?: boolean;
  /**
   * Callback executed when the Refresh products button is pressed
   *
   * @optional
   * @type {() => void}
   */
  onRefreshProducts?: () => void;
  /**
   * Whether the refresh feature flag is enabled
   *
   * @optional
   * @type {boolean}
   * @default false
   */
  isRefreshFeatureEnabled?: boolean;
  /**
   * Whether products are currently being refreshed (shows indicator)
   *
   * @optional
   * @type {boolean}
   * @default false
   */
  isRefreshing?: boolean;
  /**
   * The field currently being sorted
   * @optional
   */
  sortBy?: PdpOrderBy['field'];
  /**
   * The current sort direction
   * @optional
   */
  sortDirection?: SortDirection;
  /**
   * Callback when a sortable column header is clicked
   * @optional
   */
  onSort?: (field: PdpOrderBy['field']) => void;
}

/**
 * ProductListingTable displays a complete table of product listings with header and rows.
 * Combines the TableHeader atom and ProductListingRow molecules into a cohesive table component.
 *
 * @param props - The props for the ProductListingTable component
 * @example
 * <ProductListingTable
 *   products={[
 *     {
 *       imageSrc: "/product1.jpg",
 *       productName: "Wireless Earbuds",
 *       asin: "B0123456789",
 *       score: 85,
 *       scoreState: "ready"
 *     },
 *     {
 *       imageSrc: "/product2.jpg",
 *       productName: "Bluetooth Speaker",
 *       asin: "B0987654321",
 *       score: null,
 *       scoreState: "processing"
 *     }
 *   ]}
 *   onRowClick={(asin) => console.log('Clicked product:', asin)}
 *   isLoading={false}
 * />
 */
const ESTIMATED_ROW_HEIGHT_PX = 80;

function ProductListingTable(props: ProductListingTableProps) {
  const {
    products,
    onRowClick,
    onContactSupport,
    rowActions = [],
    className,
    'aria-label': ariaLabel = 'Product listing table',
    isLoading = false,
    totalCount,
    onLoadMore,
    isFetchingNextPage,
    importProductDialog,
    createProductDialog,
    bulkImportDialog,
    onDownloadCsv,
    isDownloadingCsv = false,
    isDownloadCsvDisabled = false,
    onRefreshProducts,
    isRefreshFeatureEnabled = false,
    isRefreshing = false,
    sortBy,
    sortDirection,
    onSort,
    ...other
  } = props;

  const tableContainerRef = React.useRef<HTMLDivElement>(null);
  const isRetailerEnabled = useFeatureFlag(FEATURE_FLAGS.APRIL_SCORING_CHANGES, false);

  const rowVirtualizer = useVirtualizer({
    count: totalCount,
    getScrollElement: () => tableContainerRef.current,
    overscan: 6,
    estimateSize: () => ESTIMATED_ROW_HEIGHT_PX,
    measureElement: (element) => element?.getBoundingClientRect?.().height ?? ESTIMATED_ROW_HEIGHT_PX,
  });

  const virtualItems = rowVirtualizer.getVirtualItems();

  const lastVirtualIndex = virtualItems.length ? virtualItems[virtualItems.length - 1].index : -1;

  useVirtualizedLoadMore({
    dataLength: products.length,
    lastVirtualIndex,
    isFetchingNextPage,
    onLoadMore,
  });

  if (isLoading) {
    return <ProductListingTableSkeleton actions={rowActions} />;
  }

  if (products.length === 0) {
    return (
      <div className={productTableWrapperClasses()}>
        <ProductListingTableHeader
          totalCount={0}
          createProductDialog={createProductDialog}
          importProductDialog={importProductDialog}
          bulkImportDialog={bulkImportDialog}
        />
        <LibraryEmptyState
          heading="No product pages found"
          description="No product pages match your search, or there are no product pages scored yet."
          icon={ClipboardIcon}
        >
          {importProductDialog}
        </LibraryEmptyState>
      </div>
    );
  }

  const showActionColumn = rowActions.length > 0;
  const colSpan = 4 + (isRetailerEnabled ? 1 : 0) + (showActionColumn ? 1 : 0);

  return (
    <div className={productTableWrapperClasses()}>
      <ProductListingTableHeader
        totalCount={totalCount}
        createProductDialog={createProductDialog}
        importProductDialog={importProductDialog}
        bulkImportDialog={bulkImportDialog}
        onDownloadCsv={onDownloadCsv}
        isDownloadingCsv={isDownloadingCsv}
        isDownloadCsvDisabled={isDownloadCsvDisabled}
        onRefreshProducts={onRefreshProducts}
        isRefreshFeatureEnabled={isRefreshFeatureEnabled}
        isRefreshing={isRefreshing}
      />
      <div ref={tableContainerRef} className={cx(tableContainerClasses(), className)}>
        <Table className={tableClasses({ withRetailer: isRetailerEnabled })} aria-label={ariaLabel} {...other}>
          <ProductListingTableThead
            showActionColumn={showActionColumn}
            sortBy={sortBy}
            sortDirection={sortDirection}
            onSort={onSort}
          />
          <TableBody>
            {virtualItems.length > 0 && (
              <>
                {(() => {
                  const first = virtualItems[0];
                  const beforeHeight = first.start;
                  return beforeHeight > 0 ? (
                    <Row aria-hidden={true}>
                      <Cell colSpan={colSpan}>
                        <div style={{ height: beforeHeight }} />
                      </Cell>
                    </Row>
                  ) : null;
                })()}

                {virtualItems.map((virtualRow) => {
                  const isDataRow = virtualRow.index < products.length;
                  const product = products[virtualRow.index];
                  return isDataRow ? (
                    <ProductListingRow
                      key={product.id}
                      data-index={virtualRow.index}
                      ref={(element: HTMLTableRowElement) => rowVirtualizer.measureElement(element)}
                      item={product}
                      onRowClick={onRowClick}
                      actions={rowActions}
                    />
                  ) : (
                    <ProductListingRow
                      data-index={virtualRow.index}
                      ref={(element: HTMLTableRowElement) => rowVirtualizer.measureElement(element)}
                      key={`skeleton-row-${virtualRow.index}`}
                      item={{
                        id: `skeleton-row-${virtualRow.index}`,
                        imageSrc: null,
                        productName: '',
                        asin: '',
                        score: null,
                        scoreStatus: null,
                        sourceType: null,
                        status: null,
                        lastRefreshed: null,
                        lastScraped: null,
                        createdAt: new Date(),
                      }}
                      isLoading={true}
                      actions={rowActions}
                    />
                  );
                })}

                {(() => {
                  const last = virtualItems[virtualItems.length - 1];
                  const afterHeight = rowVirtualizer.getTotalSize() - (last.start + last.size);
                  return afterHeight > 0 ? (
                    <Row aria-hidden={true}>
                      <Cell colSpan={colSpan}>
                        <div style={{ height: afterHeight }} />
                      </Cell>
                    </Row>
                  ) : null;
                })()}
              </>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

export { ProductListingTable };
export type { ProductListingTableProps };
